import type AutocompleteElement from '@github/auto-complete-element'
import {TemplateInstance} from '@github/template-parts'
import {debounce} from '@github/mini-throttle'
import {hideCopilotModal, showCopilotModal, addCopilotHiddenFormElement, handleCopilotAllCheckbox} from './cfb-settings'
import {on} from 'delegated-events'
import {onKey} from '@github-ui/onfocus'
import {parseHTML} from '@github-ui/parse-html'

async function submitAndUpdateForm(form: HTMLFormElement, fragment: string | undefined = undefined) {
  const body = new FormData(form)
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('type')) {
    body.append('type', urlParams.get('type')!)
  }

  if (urlParams.get('sort')) {
    body.append('sort', urlParams.get('sort')!)
  }

  if (urlParams.get('query')) {
    body.append('query', urlParams.get('query')!)
  }

  const response = await fetch(form.action, {
    method: form.method,
    body,
    headers: {Accept: 'text/fragment+html'},
  })

  // Show an error message (if request fails)
  if (response.status >= 400) {
    if (response.status === 422 && fragment === 'seat-management-csv') {
      window.location.replace(window.location.href)
    } else {
      // eslint-disable-next-line i18n-text/no-en
      const message = 'An unknown error occurred.'
      const template = document.querySelector<HTMLTemplateElement>('template.js-flash-template')!
      template.after(new TemplateInstance(template, {className: 'flash-error', message}))
    }
  } else if (fragment) {
    if (fragment === 'confirm-user-csv') {
      hideCopilotModal('confirm-user-csv')
    } else {
      hideCopilotModal('add-seat-user')
      hideCopilotModal('add-seat-team')
    }
    const userInput = document.getElementById('add-seat-user-input') as AutocompleteElement
    if (userInput) {
      userInput.value = ''
    }
    const teamInput = document.getElementById('add-seat-team-input') as AutocompleteElement
    if (teamInput) {
      teamInput.value = ''
    }
    let replaceTarget = document.getElementById('seat-management-access')
    if (fragment === 'seat-management-table') {
      replaceTarget = document.getElementById('seat-management-table')
    }
    const partial = parseHTML(document, await response.text())
    if (replaceTarget) {
      replaceTarget.replaceWith(partial)
    }
    if (fragment === 'seat-management-csv') {
      showCopilotModal('confirm-user-csv')
    }
  } else {
    const replaceTarget = form.closest('[data-replace-remote-form-target]') as HTMLElement
    const partial = parseHTML(document, await response.text())
    replaceTarget.replaceWith(partial)
    showCopilotModal('csv-confirm')
  }
}

onKey('keyup', '.js-copilot-user-input', function () {
  const button = document.getElementById('js-user-copilot-add-user')! as HTMLButtonElement
  const input = document.querySelector<HTMLInputElement>('.js-copilot-user-input')
  if (input && input.value) {
    button.disabled = false
  } else {
    button.disabled = true
  }
})

onKey('keyup', '.js-copilot-team-input', function () {
  const button = document.getElementById('js-team-copilot-add-team')! as HTMLButtonElement
  const input = document.querySelector<HTMLInputElement>('.js-copilot-team-input')
  if (input && input.value) {
    button.disabled = false
  } else {
    button.disabled = true
  }
})

const debouncedSearch = debounce(searchExistingSeats, 200)
onKey('keyup', '.js-copilot-org-search', function () {
  debouncedSearch()
})

on('auto-complete-change', '.js-copilot-selected-users-add-auto-complete', function (event) {
  const autoComplete = event.target as AutocompleteElement
  if (!autoComplete.value) {
    return
  }

  const input = autoComplete.querySelector<HTMLInputElement>('.js-copilot-selected-users-add-input')
  input!.value = autoComplete.value
})

on('auto-complete-change', '.js-copilot-selected-teams-add-auto-complete', function (event) {
  const autoComplete = event.target as AutocompleteElement
  if (!autoComplete.value) {
    return
  }

  const input = autoComplete.querySelector<HTMLInputElement>('.js-copilot-selected-teams-add-input')
  input!.value = autoComplete.value
})

on('click', '.js-add-copilot-csv-users', function (event) {
  event.preventDefault()
  const input = event.target as HTMLInputElement
  const form = input.closest('form') as HTMLFormElement
  submitAndUpdateForm(form, 'confirm-user-csv')
})

on('click', '.js-user-copilot-modal-open', function (event) {
  event.preventDefault()
  const input = document.querySelector<HTMLInputElement>('.js-copilot-user-input')
  input!.value = ''
  const button = document.getElementById('js-user-copilot-add-user')! as HTMLButtonElement
  button.disabled = true
  showCopilotModal('add-seat-user')
})

on('click', '.js-user-copilot-add-user', async function () {
  const form = document.getElementById('copilot-add-user-form') as HTMLFormElement
  await submitAndUpdateForm(form, 'seat-management-access')
})

on('click', '.js-team-copilot-modal-open', function (event) {
  event.preventDefault()
  const input = document.querySelector<HTMLInputElement>('.js-copilot-team-input')
  input!.value = ''
  const button = document.getElementById('js-team-copilot-add-team')! as HTMLButtonElement
  button.disabled = true
  showCopilotModal('add-seat-team')
})

on('click', '.js-team-copilot-add-user', function (event) {
  const input = event.target as HTMLInputElement
  const form = input.closest('form') as HTMLFormElement
  submitAndUpdateForm(form, 'seat-management-access')
})

on('click', '.js-team-copilot-reenable-seat', async function (event) {
  const input = event.target as HTMLInputElement
  const form = input.closest('form') as HTMLFormElement
  await submitAndUpdateForm(form, 'seat-management-access')
})

on('click', '.js-team-copilot-remove-seat', async function (event) {
  const input = event.target as HTMLInputElement
  const form = input.closest('form') as HTMLFormElement
  await submitAndUpdateForm(form, 'seat-management-access')
})

on('click', '.js-copilot-type-filter', async function (event) {
  const target = event.target as HTMLElement
  event.preventDefault()
  const urlParams = new URLSearchParams(window.location.search)
  const type = target.getAttribute('data-type')!
  urlParams.set('type', type)
  const url = `${window.location.pathname}?${urlParams.toString()}`
  window.history.pushState({}, '', url)
  const form = target.closest('form') as HTMLFormElement
  submitAndUpdateForm(form, 'seat-management-access')
})

on('click', '.js-copilot-select-all-seats', function (event) {
  const target = event.target as HTMLInputElement
  const checkboxes = document.querySelectorAll<HTMLInputElement>('.js-copilot-bulk-actions-checkbox')
  let count = 0
  let isBulkRemovable = false
  for (const checkbox of checkboxes) {
    if (target.checked) {
      checkbox.checked = true
      const bulkActions = document.getElementById('seat-management-bulk-menu')
      bulkActions!.hidden = false
      const bulkText = document.getElementById('selected-member-count')
      count += 1
      bulkText!.textContent = `${count} ${pluralize(count)} selected`
      const breakdown = document.getElementById('seat-management-breakdown')
      breakdown!.hidden = true
      if (checkbox.getAttribute('data-removable') !== null) {
        isBulkRemovable = true
      }
    } else {
      const bulkActions = document.getElementById('seat-management-bulk-menu')
      bulkActions!.hidden = true
      const breakdown = document.getElementById('seat-management-breakdown')
      breakdown!.hidden = false
      checkbox.checked = false
    }
  }
  const removeButton = document.getElementById('js-remove-copilot-seats') as HTMLButtonElement
  removeButton.disabled = !isBulkRemovable
})

on('click', '#js-remove-copilot-seats', function (event) {
  const target = event.target as HTMLElement
  const bulkForm = target.closest('form') as HTMLFormElement
  const checkboxes = document.querySelectorAll<HTMLInputElement>('.js-copilot-bulk-actions-checkbox')
  for (const checkbox of checkboxes) {
    if (checkbox.checked) {
      addCopilotHiddenFormElement(bulkForm, checkbox.name, checkbox.value)
    }
  }
  submitAndUpdateForm(bulkForm, 'seat-management-access')
})

on('change', '.js-copilot-file-input', function (event) {
  const input = event.target as HTMLInputElement
  const form = input.closest('form') as HTMLFormElement

  if (input.files && input.files.length > 0 && input.files[0]!.size > 0 && input.files[0]!.size <= 2097152) {
    submitAndUpdateForm(form, 'seat-management-csv')
  } else {
    // eslint-disable-next-line i18n-text/no-en
    alert('Maximum file size is 2 megabytes. Please try again.')
    event.preventDefault()
  }
})

on('change', '.js-copilot-bulk-actions-checkbox', function () {
  const checkboxes = document.querySelectorAll<HTMLInputElement>('.js-copilot-bulk-actions-checkbox')
  let found = false
  let count = 0
  let totalCount = 0
  let isBulkRemovable = false
  for (const checkbox of checkboxes) {
    totalCount += 1
    if (checkbox.checked) {
      const bulkActions = document.getElementById('seat-management-bulk-menu')
      bulkActions!.hidden = false
      const bulkText = document.getElementById('selected-member-count')
      count += 1
      bulkText!.textContent = `${count} ${pluralize(count)} selected`
      const breakdown = document.getElementById('seat-management-breakdown')
      breakdown!.hidden = true
      found = true
      if (checkbox.getAttribute('data-removable') !== null) {
        isBulkRemovable = true
      }
    }
  }
  const allCheckbox = document.getElementById('select-all-checkbox') as HTMLInputElement
  handleCopilotAllCheckbox(allCheckbox, count, totalCount)

  if (found === false) {
    const bulkActions = document.getElementById('seat-management-bulk-menu')
    bulkActions!.hidden = true
    const breakdown = document.getElementById('seat-management-breakdown')
    breakdown!.hidden = false
  }
  const removeButton = document.getElementById('js-remove-copilot-seats') as HTMLButtonElement
  removeButton.disabled = !isBulkRemovable
})

function searchExistingSeats() {
  const input = document.querySelector<HTMLInputElement>('.js-copilot-org-search')!
  let query
  if (input && input.value) {
    query = input.value
  }

  const urlParams = new URLSearchParams(window.location.search)
  urlParams.set('query', query || '')
  const url = `${window.location.pathname}?${urlParams.toString()}`
  window.history.pushState({}, '', url)
  const form = input.closest('form') as HTMLFormElement
  submitAndUpdateForm(form, 'seat-management-table')
}

function pluralize(count: number) {
  return count === 1 ? 'member' : 'members'
}
